import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Businesses from './pages/Businesses';
import BanquetHalls from './pages/BanquetHalls';
import BanquetBookings from './pages/BanquetBookings';
import MenuItems from './pages/MenuItems';
import MerchantMenuItems from './pages/MerchantMenuItems';
import MerchantBanquetHalls from './pages/MerchantBanquetHalls';
import MerchantBanquetBookings from './pages/MerchantBanquetBookings';
import MerchantBusiness from './pages/MerchantBusiness';
import MerchantNotifications from './pages/MerchantNotifications';
import MerchantNotificationHistory from './pages/MerchantNotificationHistory';
import SurpriseBoxes from './pages/SurpriseBoxes';
import Orders from './pages/Orders';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Restaurants from './pages/Restaurants';
import RestaurantDetail from './pages/RestaurantDetail';
import MenuCart from './pages/MenuCart';
import MenuCheckout from './pages/MenuCheckout';
import OrderDetails from './pages/OrderDetails';
import Categories from './pages/Categories';
import Payments from './pages/Payments';
import Reviews from './pages/Reviews';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Support from './pages/Support';
import Settings from './pages/Settings';
import ReturnsRefunds from './pages/ReturnsRefunds';
import AccessManagement from './pages/AccessManagement';
import Layout from './components/Layout/Layout';

/**
 * BiteHub theme — "premium desert / frankincense", matching the mobile app.
 * A deep frankincense teal carries primary actions, muted gold is the single
 * accent (ratings, savings, offers), and everything sits on warm sand
 * neutrals rather than cool greys. This theme intentionally drives the look
 * of every page via component styleOverrides, so most pages need no per-page
 * styling to match.
 */

// Design tokens — the BiteHub logo's palette, kept in step with Flutter's
// AppTheme: deep forest green primary, the logo's orange as the single
// accent, everything on the logo's cream.
const BRAND = '#0B5E38'; // logo green, lightened enough for white text
const BRAND_LIGHT = '#12784A';
const BRAND_DARK = '#054026';
const BRAND_GREEN = BRAND; // legacy alias used further down
const GOLD = '#E13407'; // the logo's orange — accent only
const GOLD_DEEP = '#B5290A';
const GOLD_TINT = '#FDEAE1';
const INK = '#16211A'; // green-black text
const CANVAS = '#FAF6EE'; // logo cream app background
const BORDER = '#ECE4D8'; // hairline borders / dividers
const BORDER_STRONG = '#DBCFBE';
const MUTED = '#6A6E64'; // secondary text

// Soft tonal backgrounds + strong ink for status pills (Chip) and dots
const STATUS = {
  success: { fg: BRAND, bg: '#E6F0EA' },
  warning: { fg: GOLD_DEEP, bg: GOLD_TINT },
  error: { fg: '#B3261E', bg: '#F9E7E5' },
  info: { fg: '#2F6B88', bg: '#E7F0F4' },
  primary: { fg: BRAND_DARK, bg: '#E6F0EA' },
  default: { fg: MUTED, bg: '#F4EDE1' },
} as const;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: BRAND, light: BRAND_LIGHT, dark: BRAND_DARK, contrastText: '#FFFFFF' },
    secondary: { main: GOLD, light: '#F88716', dark: GOLD_DEEP, contrastText: '#FFFFFF' },
    success: { main: BRAND, light: BRAND_LIGHT, dark: BRAND_DARK, contrastText: '#FFFFFF' },
    warning: { main: '#B07A1E', light: '#D8AE55', dark: '#8A5B12', contrastText: '#FFFFFF' },
    error: { main: '#B3261E', light: '#D2584F', dark: '#8A1C16', contrastText: '#FFFFFF' },
    info: { main: '#2F6B88', light: '#5C93AD', dark: '#224E63', contrastText: '#FFFFFF' },
    background: { default: CANVAS, paper: '#FFFFFF' },
    text: { primary: INK, secondary: MUTED, disabled: '#A2A197' },
    divider: BORDER,
    action: {
      hover: 'rgba(35,31,22,0.04)',
      selected: 'rgba(11,94,56,0.08)',
      active: MUTED,
      disabled: 'rgba(22,33,26,0.34)',
      disabledBackground: 'rgba(22,33,26,0.10)',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    fontSize: 14,
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 500 },
    caption: { color: MUTED },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: CANVAS,
          color: INK,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#DBCFBE',
          borderRadius: 8,
          border: '2px solid ' + CANVAS,
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#C9B99F' },
        '::selection': { backgroundColor: 'rgba(11,94,56,0.16)' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        // Default <Paper> surfaces (elevation 1) read as flat, bordered cards.
        elevation1: { boxShadow: 'none', border: '1px solid ' + BORDER },
        // Menus / popovers (elevation 8) keep a soft shadow so they detach.
        elevation8: {
          boxShadow: '0 10px 30px rgba(35,31,22,0.12)',
          border: '1px solid ' + BORDER,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid ' + BORDER,
          borderRadius: 14,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 500, borderRadius: 10, boxShadow: 'none' },
        // primary.main is already near-black, so the palette gives contained
        // buttons their dark fill + black hover. We must NOT force a background
        // here unconditionally — that overrode MUI's disabled background and
        // left a faded label on a black box (the "blank disabled button" bug).
        // Only keep the disabled state legible.
        contained: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(17,17,15,0.10)',
            color: 'rgba(17,17,15,0.34)',
          },
        },
        outlined: {
          borderColor: BORDER_STRONG,
          color: INK,
          backgroundColor: '#FFFFFF',
          '&:hover': { borderColor: '#DBCFBE', backgroundColor: 'rgba(17,17,15,0.03)' },
          '&.Mui-disabled': {
            color: 'rgba(17,17,15,0.34)',
            borderColor: BORDER,
            backgroundColor: '#FFFFFF',
          },
        },
        text: {
          color: INK,
          '&:hover': { backgroundColor: 'rgba(17,17,15,0.04)' },
          '&.Mui-disabled': { color: 'rgba(17,17,15,0.34)' },
        },
        sizeSmall: { padding: '5px 12px' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 10, color: MUTED, '&:hover': { backgroundColor: 'rgba(17,17,15,0.05)' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const key = (ownerState.color && ownerState.color !== 'default'
            ? ownerState.color
            : 'default') as keyof typeof STATUS;
          const tone = STATUS[key] || STATUS.default;
          const outlined = ownerState.variant === 'outlined';
          return {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: 12,
            height: 24,
            border: outlined ? '1px solid ' + tone.fg + '55' : 'none',
            backgroundColor: outlined ? 'transparent' : tone.bg,
            color: tone.fg,
            '& .MuiChip-label': {
              display: 'inline-flex',
              alignItems: 'center',
              paddingLeft: 10,
              paddingRight: 10,
              '&::before': {
                content: '""',
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: tone.fg,
                marginRight: 7,
                flexShrink: 0,
              },
            },
            '& .MuiChip-deleteIcon': { color: tone.fg, opacity: 0.6, '&:hover': { color: tone.fg, opacity: 1 } },
          };
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: '#F4EDE1', borderRadius: 10, padding: 3, gap: 2 },
        grouped: {
          border: '0 !important',
          borderRadius: '8px !important',
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: 13,
          color: MUTED,
          padding: '5px 14px',
          '&:hover': { backgroundColor: 'rgba(17,17,15,0.04)' },
          '&.Mui-selected': {
            backgroundColor: '#FFFFFF',
            color: INK,
            boxShadow: '0 1px 2px rgba(17,17,15,0.12)',
            '&:hover': { backgroundColor: '#FFFFFF' },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_STRONG },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#DBCFBE' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_GREEN, borderWidth: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { '&.Mui-focused': { color: BRAND_GREEN } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: BORDER, padding: '12px 16px' },
        head: {
          textTransform: 'uppercase' as const,
          fontSize: 11,
          letterSpacing: '0.06em',
          fontWeight: 500,
          color: MUTED,
          backgroundColor: 'transparent',
          borderBottom: '1px solid ' + BORDER,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:last-child td': { borderBottom: 0 } },
      },
    },
    MuiTableContainer: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: 'none', backgroundImage: 'none' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', borderColor: BORDER },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: BORDER } },
    },
    MuiLink: {
      styleOverrides: { root: { color: BRAND_GREEN, textDecorationColor: 'rgba(11,94,56,0.4)' } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: INK, fontSize: 12, borderRadius: 8, padding: '6px 10px' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: BRAND_GREEN, height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 600, '&.Mui-selected': { color: INK } },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="businesses" element={<Businesses />} />
              <Route path="banquet-halls" element={<BanquetHalls />} />
              <Route path="banquet-bookings" element={<BanquetBookings />} />
              <Route path="menu-items" element={<MenuItems />} />
              <Route path="my-business" element={<MerchantBusiness />} />
              <Route path="my-menu-items" element={<MerchantMenuItems />} />
              <Route path="my-banquet-halls" element={<MerchantBanquetHalls />} />
              <Route path="my-banquet-bookings" element={<MerchantBanquetBookings />} />
              <Route path="merchant-notifications" element={<MerchantNotifications />} />
              <Route path="merchant-notification-history" element={<MerchantNotificationHistory />} />
              <Route path="surprise-boxes" element={<SurpriseBoxes />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:orderId" element={<OrderDetails />} />
              <Route path="cart" element={<Cart />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="restaurants" element={<Restaurants />} />
              <Route path="restaurants/:id" element={<RestaurantDetail />} />
              <Route path="menu-cart" element={<MenuCart />} />
              <Route path="menu-checkout" element={<MenuCheckout />} />
              <Route path="categories" element={<Categories />} />
              <Route path="payments" element={<Payments />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="support" element={<Support />} />
              <Route path="settings" element={<Settings />} />
              <Route path="returns-refunds" element={<ReturnsRefunds />} />
              <Route
                path="access-management"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AccessManagement />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
